import { listRatingScaleValues } from '@/shared-question-model';

import type { SpokenAffordances } from './shared-responder-v2-core-handsfree.types';
import {
  pickSingleSpokenValue,
  resolveOptionDisplayLabels,
} from './shared-responder-v2-core-option.helpers';
import type {
  SpokenPickExtractionInput,
  SpokenPickOption,
} from './shared-responder-v2-core-spoken-pick-port.types';
import {
  SPOKEN_PICK_SHAPES,
  type SpokenPickShape,
} from './shared-responder-v2-core-spoken-pick.consts';
import type { ResponderDraftEvent } from './shared-responder-v2-core-thread-behaviour';
import { THREAD_KINDS } from './shared-responder-v2-core-thread.consts';
import type { QuestionSpec } from './shared-responder-v2-core-thread.types';

const NON_SPEECH_CHARACTERS = /[^\p{L}\p{N}\s]/gu;

const WHITESPACE_RUN = /\s+/gu;

const WHITESPACE_SEPARATOR = /\s+/u;

const SCALE_MAXIMUM_LEAD_IN = ['out', 'of'] as const;

export function normalizeSpokenText(text: string): string {
  return text
    .toLowerCase()
    .replace(NON_SPEECH_CHARACTERS, ' ')
    .replace(WHITESPACE_RUN, ' ')
    .trim();
}

function toSpokenWords(text: string): string[] {
  const normalized = normalizeSpokenText(text);

  return normalized === '' ? [] : normalized.split(' ');
}

function resolveCommandPhrases(commands: readonly string[]): string[][] {
  return commands
    .map(toSpokenWords)
    .filter((words) => words.length > 0)
    .toSorted((left, right) => right.length - left.length);
}

function endsWithPhrase(
  words: readonly string[],
  phrase: readonly string[]
): boolean {
  if (phrase.length > words.length) {
    return false;
  }

  const tail = words.slice(words.length - phrase.length);

  return tail.every((word, index) => word === phrase[index]);
}

export function endsWithSpokenCommand(
  transcript: string,
  commands: readonly string[]
): boolean {
  const spoken = toSpokenWords(transcript);

  return resolveCommandPhrases(commands).some((phrase) =>
    endsWithPhrase(spoken, phrase)
  );
}

function isSamePhrase(
  words: readonly string[],
  phrase: readonly string[]
): boolean {
  return words.length === phrase.length && endsWithPhrase(words, phrase);
}

function findTrailingCommandStart(
  words: readonly string[],
  phrases: readonly (readonly string[])[]
): number | null {
  const longestPhrase = phrases[0]?.length ?? 0;

  for (let start = words.length - 1; start >= 0; start -= 1) {
    const tail = toSpokenWords(words.slice(start).join(' '));

    if (tail.length > longestPhrase) {
      return null;
    }

    if (phrases.some((phrase) => isSamePhrase(tail, phrase))) {
      return start;
    }
  }

  return null;
}

export function stripSpokenContinue(
  transcript: string,
  commands: readonly string[]
): string {
  const trimmed = transcript.trim();
  const phrases = resolveCommandPhrases(commands);

  if (phrases.length === 0 || trimmed === '') {
    return trimmed;
  }

  let words = trimmed.split(WHITESPACE_SEPARATOR);
  let start = findTrailingCommandStart(words, phrases);

  while (start !== null) {
    words = words.slice(0, start);
    start = findTrailingCommandStart(words, phrases);
  }

  return words.join(' ').trim();
}

export interface DictatedCommit {
  readonly text: string;
  readonly advances: boolean;
}

export function resolveDictatedCommit(args: {
  readonly transcript: string;
  readonly commands: readonly string[];
  readonly awaitsSpokenContinue: boolean;
}): DictatedCommit {
  const { awaitsSpokenContinue, commands, transcript } = args;

  if (!awaitsSpokenContinue || !endsWithSpokenCommand(transcript, commands)) {
    return { text: transcript, advances: false };
  }

  return {
    text: stripSpokenContinue(transcript, commands),
    advances: true,
  };
}

export function resolveSpokenPickShape(spec: QuestionSpec): SpokenPickShape {
  if (spec.kind === THREAD_KINDS.Rating) {
    return SPOKEN_PICK_SHAPES.Rating;
  }

  return spec.kind === THREAD_KINDS.Ranking
    ? SPOKEN_PICK_SHAPES.Ranking
    : SPOKEN_PICK_SHAPES.Choice;
}

export function resolveSpokenPickOptions(
  spec: QuestionSpec
): readonly SpokenPickOption[] {
  if (spec.optionValues.length > 0) {
    const labels = resolveOptionDisplayLabels(spec);

    return spec.optionValues.map((value, index) => ({
      label: labels[index] ?? value,
      value,
    }));
  }

  if (spec.kind !== THREAD_KINDS.Rating) {
    return [];
  }

  return listRatingScaleValues(spec).map((scaleValue) => {
    const value = String(scaleValue);

    return { label: value, value };
  });
}

interface SpokenPickPhrase {
  readonly words: readonly string[];
  readonly value: string;
}

function toPhraseKey(words: readonly string[]): string {
  return words.join(' ');
}

function dropAmbiguousPhrases(
  phrases: readonly SpokenPickPhrase[]
): readonly SpokenPickPhrase[] {
  const valuesByPhrase = new Map<string, Set<string>>();

  for (const phrase of phrases) {
    const key = toPhraseKey(phrase.words);
    const values = valuesByPhrase.get(key) ?? new Set<string>();

    values.add(phrase.value);
    valuesByPhrase.set(key, values);
  }

  return phrases.filter(
    (phrase) => valuesByPhrase.get(toPhraseKey(phrase.words))?.size === 1
  );
}

function resolveSpokenPickPhrases(
  input: SpokenPickExtractionInput
): readonly SpokenPickPhrase[] {
  const namesValue = input.shape === SPOKEN_PICK_SHAPES.Rating;

  const phrases = input.options
    .flatMap((option) => [
      { text: option.label, value: option.value },
      ...(namesValue ? [{ text: option.value, value: option.value }] : []),
    ])
    .map(({ text, value }) => ({ words: toSpokenWords(text), value }))
    .filter((phrase) => phrase.words.length > 0);

  return dropAmbiguousPhrases(phrases);
}

function picksOneValue(input: SpokenPickExtractionInput): boolean {
  return (
    input.shape === SPOKEN_PICK_SHAPES.Rating ||
    (input.shape === SPOKEN_PICK_SHAPES.Choice && !input.isMultiple)
  );
}

function isScaleMaximumMention(
  spoken: readonly string[],
  index: number
): boolean {
  return endsWithPhrase(spoken.slice(0, index), SCALE_MAXIMUM_LEAD_IN);
}

function isUnclaimedMatch(
  spoken: readonly string[],
  claimed: readonly boolean[],
  phrase: readonly string[],
  start: number
): boolean {
  return phrase.every(
    (word, offset) =>
      spoken[start + offset] === word && !claimed[start + offset]
  );
}

export function matchSpokenPickLocally(
  input: SpokenPickExtractionInput
): readonly string[] {
  const spoken = toSpokenWords(input.transcript);
  const claimed = spoken.map(
    (_, index) =>
      input.shape === SPOKEN_PICK_SHAPES.Rating &&
      isScaleMaximumMention(spoken, index)
  );
  const found: { readonly position: number; readonly value: string }[] = [];

  const phrases = resolveSpokenPickPhrases(input).toSorted(
    (left, right) => right.words.length - left.words.length
  );

  for (const phrase of phrases) {
    for (
      let start = 0;
      start + phrase.words.length <= spoken.length;
      start += 1
    ) {
      if (isUnclaimedMatch(spoken, claimed, phrase.words, start)) {
        phrase.words.forEach((_, offset) => {
          claimed[start + offset] = true;
        });
        found.push({ position: start, value: phrase.value });
      }
    }
  }

  const values = found
    .toSorted((left, right) => left.position - right.position)
    .map((entry) => entry.value);
  const byLastMention = [...new Set(values.toReversed())].toReversed();

  if (!picksOneValue(input)) {
    return byLastMention;
  }

  const single = pickSingleSpokenValue(byLastMention);

  return single === undefined ? [] : [single];
}

export function resolveSpokenDraftEvent(args: {
  readonly transcript: string;
  readonly affordances: SpokenAffordances;
  readonly commands: readonly string[];
  readonly isAwaitingOtherText?: boolean;
}): ResponderDraftEvent | null {
  const {
    affordances,
    commands,
    isAwaitingOtherText = false,
    transcript,
  } = args;

  if (isAwaitingOtherText) {
    const otherText = stripSpokenContinue(transcript, commands);

    return otherText.trim() === ''
      ? null
      : { type: 'otherTextChanged', text: otherText };
  }

  if (!affordances.draftsSpokenAnswer) {
    return null;
  }

  const spoken = stripSpokenContinue(transcript, commands);

  return spoken.trim() === ''
    ? null
    : { type: 'transcriptChanged', transcript: spoken };
}
